import type { CoachServiceCorrectionOperatorResult } from "@billiard/contracts";
import {
  type CoachServiceCorrectionCommand,
  type CoachServiceCorrectionContext,
  coachServiceCorrectionCommandIsValid,
  getCoachServiceCorrectionByRequest,
} from "./coachServiceCorrectionApi";

export const COACH_SERVICE_CORRECTION_RECOVERY_STORAGE_KEY =
  "billiard.ops-web.coach-service-correction-recovery.v1";

export type CoachServiceCorrectionRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN" | "RETRY_ALLOWED";

export interface CoachServiceCorrectionRecovery extends CoachServiceCorrectionContext {
  command: CoachServiceCorrectionCommand;
  state: CoachServiceCorrectionRecoveryState;
}

export type CoachServiceCorrectionRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: CoachServiceCorrectionRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH" | "COMMAND_MISMATCH"; canRetry: false }
  | { status: "STORAGE_UNAVAILABLE"; canRetry: false; pendingOutcomeMayExist: boolean };

export type CoachServiceCorrectionAuthorityResolution =
  | { status: "EXECUTED"; result: CoachServiceCorrectionOperatorResult }
  | { status: "RETRY_ALLOWED"; recovery: CoachServiceCorrectionRecovery }
  | { status: "NOT_EXECUTED_REFRESH_REQUIRED"; command: CoachServiceCorrectionCommand }
  | { status: "FAIL_CLOSED" };

let pendingRecovery: CoachServiceCorrectionRecovery | null = null;
let dispatchingRecovery: CoachServiceCorrectionRecovery | null = null;
let hydrated = false;
let storageUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function exactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function contextMatches(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.actorId === context.actorId
  );
}

function immutableRecovery(
  context: CoachServiceCorrectionContext,
  command: CoachServiceCorrectionCommand,
  state: CoachServiceCorrectionRecoveryState,
): CoachServiceCorrectionRecovery {
  const value = structuredClone({ ...context, command, state }) as CoachServiceCorrectionRecovery;
  const freezeDeep = (item: unknown): void => {
    if (!item || typeof item !== "object" || Object.isFrozen(item)) return;
    for (const child of Object.values(item)) freezeDeep(child);
    Object.freeze(item);
  };
  freezeDeep(value);
  return value;
}

function storedRecovery(value: unknown): CoachServiceCorrectionRecovery | null {
  if (
    !isRecord(value) ||
    !exactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !exactKeys(value.recovery, ["tenantId", "storeId", "actorId", "command", "state"])
  ) {
    return null;
  }
  const tenantId = value.recovery.tenantId;
  const storeId = value.recovery.storeId;
  const actorId = value.recovery.actorId;
  if (
    typeof tenantId !== "string" ||
    typeof storeId !== "string" ||
    typeof actorId !== "string" ||
    !coachServiceCorrectionCommandIsValid(
      value.recovery.command as CoachServiceCorrectionCommand,
    ) ||
    !["CLAIMED", "OUTCOME_UNKNOWN", "RETRY_ALLOWED"].includes(String(value.recovery.state))
  ) {
    return null;
  }
  const context: CoachServiceCorrectionContext = { tenantId, storeId, actorId };
  const command = value.recovery.command as CoachServiceCorrectionCommand;
  if (
    command.context.tenantId !== context.tenantId ||
    command.context.storeId !== context.storeId ||
    command.context.actorId !== context.actorId
  ) {
    return null;
  }
  return immutableRecovery(
    context,
    command,
    value.recovery.state as CoachServiceCorrectionRecoveryState,
  );
}

function storageAccess(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function recoveryEquals(
  left: CoachServiceCorrectionRecovery,
  right: CoachServiceCorrectionRecovery,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readStored(storage: Storage): CoachServiceCorrectionRecovery | null {
  const encoded = storage.getItem(COACH_SERVICE_CORRECTION_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: CoachServiceCorrectionRecovery): boolean {
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    storage.setItem(
      COACH_SERVICE_CORRECTION_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const saved = readStored(storage);
    return saved !== null && recoveryEquals(saved, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const storage = storageAccess();
  if (!storage) {
    storageUnavailable = true;
    return;
  }
  try {
    const encoded = storage.getItem(COACH_SERVICE_CORRECTION_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      storageUnavailable = true;
      return;
    }
    pendingRecovery =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(pendingRecovery)) storageUnavailable = true;
  } catch {
    storageUnavailable = true;
  }
}

function unavailableView(): CoachServiceCorrectionRecoveryView {
  return { status: "STORAGE_UNAVAILABLE", canRetry: false, pendingOutcomeMayExist };
}

export function coachServiceCorrectionContext(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): CoachServiceCorrectionContext {
  return { tenantId: operator.tenantId, storeId: operator.storeId, actorId: operator.id };
}

export function readCoachServiceCorrectionRecovery(
  context: CoachServiceCorrectionContext,
): CoachServiceCorrectionRecoveryView {
  hydrate();
  if (storageUnavailable) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "RETRY_ALLOWED",
  };
}

export function coachServiceCorrectionRecoveryBlocksWrites(): boolean {
  hydrate();
  return storageUnavailable || pendingRecovery !== null;
}

export function claimCoachServiceCorrectionRecovery(
  context: CoachServiceCorrectionContext,
  command: CoachServiceCorrectionCommand,
): CoachServiceCorrectionRecoveryView {
  const current = readCoachServiceCorrectionRecovery(context);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (
    !coachServiceCorrectionCommandIsValid(command) ||
    command.context.tenantId !== context.tenantId ||
    command.context.storeId !== context.storeId ||
    command.context.actorId !== context.actorId
  ) {
    storageUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableRecovery(context, command, "CLAIMED");
  if (!saveStored(recovery)) {
    storageUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verify(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
): boolean {
  hydrate();
  if (storageUnavailable || pendingRecovery !== recovery || !contextMatches(recovery, context)) {
    return false;
  }
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    const stored = readStored(storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      storageUnavailable = true;
      return false;
    }
    return true;
  } catch {
    storageUnavailable = true;
    return false;
  }
}

export function beginCoachServiceCorrectionDispatch(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
): boolean {
  if (dispatchingRecovery || recovery.state === "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return false;
  }
  dispatchingRecovery = recovery;
  return true;
}

export function finishCoachServiceCorrectionDispatch(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCoachServiceCorrectionOutcomeUnknown(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
): boolean {
  if (!verify(recovery, context)) return false;
  const unknown = immutableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    storageUnavailable = true;
    return false;
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = unknown;
  return true;
}

export function clearCoachServiceCorrectionRecovery(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
): boolean {
  if (!verify(recovery, context)) return false;
  if (typeof window !== "undefined") {
    const storage = storageAccess();
    if (!storage) {
      storageUnavailable = true;
      return false;
    }
    try {
      storage.removeItem(COACH_SERVICE_CORRECTION_RECOVERY_STORAGE_KEY);
      if (storage.getItem(COACH_SERVICE_CORRECTION_RECOVERY_STORAGE_KEY) !== null) {
        storageUnavailable = true;
        return false;
      }
    } catch {
      storageUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function reconcileCoachServiceCorrectionAuthority(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
  result: CoachServiceCorrectionOperatorResult | null,
): CoachServiceCorrectionAuthorityResolution {
  if (recovery.state !== "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return { status: "FAIL_CLOSED" };
  }
  if (result !== null) {
    return clearCoachServiceCorrectionRecovery(recovery, context)
      ? { status: "EXECUTED", result }
      : { status: "FAIL_CLOSED" };
  }
  if (Date.parse(recovery.command.preview.generatedAt) + 5 * 60_000 <= Date.now()) {
    const command = recovery.command;
    return clearCoachServiceCorrectionRecovery(recovery, context)
      ? { status: "NOT_EXECUTED_REFRESH_REQUIRED", command }
      : { status: "FAIL_CLOSED" };
  }
  const retryable = immutableRecovery(context, recovery.command, "RETRY_ALLOWED");
  if (!saveStored(retryable)) {
    storageUnavailable = true;
    return { status: "FAIL_CLOSED" };
  }
  pendingRecovery = retryable;
  return { status: "RETRY_ALLOWED", recovery: retryable };
}

export async function resolveCoachServiceCorrectionAuthority(
  recovery: CoachServiceCorrectionRecovery,
  context: CoachServiceCorrectionContext,
  signal?: AbortSignal,
): Promise<CoachServiceCorrectionAuthorityResolution> {
  if (recovery.state !== "OUTCOME_UNKNOWN" || !verify(recovery, context)) {
    return { status: "FAIL_CLOSED" };
  }
  const result = await getCoachServiceCorrectionByRequest(recovery.command, signal);
  return reconcileCoachServiceCorrectionAuthority(recovery, context, result);
}

export function resetCoachServiceCorrectionRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
  try {
    storageAccess()?.removeItem(COACH_SERVICE_CORRECTION_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts with a fresh fail-closed state.
  }
}

export function resetCoachServiceCorrectionRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  storageUnavailable = false;
  pendingOutcomeMayExist = false;
}
